import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ChipRow, EmptyState, Pill, Spinner } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { Plus, Search } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unconverted", label: "Unconverted" },
  { key: "converted", label: "Converted" },
  { key: "regular_production", label: "Regular" },
];

export default function Customers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [initialSent, setInitialSent] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (filter !== "all") sp.set("status", filter);
    if (q) sp.set("q", q);
    try {
      const [customers, initialDocs] = await Promise.all([
        api.get(`/customers?${sp}`),
        api.get(`/quotes?quote_type=initial_meeting`).catch(() => []),
      ]);
      setItems(customers);
      setInitialSent(new Set((initialDocs || []).map((d) => d.customer_id).filter(Boolean)));
    } finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const toneFor = (status) =>
    status === "converted" ? "success" : status === "regular_production" ? "brand" : "neutral";

  return (
    <>
      <AppHeader title="Customers" />
      <div className="sg-main sg-fade-in">
        <div style={{ position: "relative", marginBottom: 12 }}>
          <Search size={16} color="var(--muted)" style={{ position: "absolute", left: 14, top: 15 }} />
          <input
            className="sg-input"
            placeholder="Search company, brand, contact"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ paddingLeft: 40 }}
            data-testid="customers-search"
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <ChipRow items={FILTERS} value={filter} onChange={setFilter} testIdPrefix="customers-filter" />
        </div>

        {loading ? <Spinner /> : items.length === 0 ? (
          <EmptyState title="No customers yet" hint="Tap + to add a new customer." />
        ) : (
          items.map((c) => {
            const hasInitialDoc = initialSent.has(c.customer_id);
            return (
              <div
                key={c.customer_id}
                onClick={() => navigate(`/customers/${c.customer_id}`)}
                className="sg-list-card"
                data-testid={`customer-row-${c.customer_id}`}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.company_name}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{c.brand_name}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                    <span
                      data-testid={`customer-initial-status-${c.customer_id}`}
                      style={{
                        fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase",
                        padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap",
                        background: hasInitialDoc ? "#DCFCE7" : "#FEE2E2",
                        color: hasInitialDoc ? "#166534" : "#B91C1C",
                        border: `1px solid ${hasInitialDoc ? "#86EFAC" : "#FCA5A5"}`,
                      }}
                    >
                      {hasInitialDoc ? "Initial Sent" : "Initial Pending"}
                    </span>
                    <Pill label={c.status} tone={toneFor(c.status)} />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
                  {c.contact_person} · {c.phone}
                </div>
              </div>
            );
          })
        )}
      </div>

      <button
        className="sg-fab"
        onClick={() => navigate("/customers/new")}
        data-testid="customers-add-button"
        aria-label="Add customer"
      >
        <Plus size={24} />
      </button>
    </>
  );
}
